import { EventAttendeeTypeEnum } from '../enums/event-attendee-type.enum';
import { IBmTicketStatus, IBtrPassportApiModel } from './api';
import {
  BtrEventModel,
  IAttendeeInformation,
  IAttendeeItemToServer,
  mapPassportBeaconGroups,
  mapVerificationTiers,
  NewAttendeeInformation,
} from './btr-event.model';
import { BtrStaticPageModel } from './btr-static-page.model';
import { LocationModel } from './location.model';
import { TicketModel } from './ticket.model';

export class BtrPassportModel extends BtrEventModel {
  descriptionPage?: BtrStaticPageModel;
  isRefundStripeFeeOnClient: boolean;
  isExpired: boolean;
  timezone: string;
  datePublication: string;
  dateArchiving: string;
  dateCreation: string;
  whiteLabelName: string;
  attendeeInfo: Array<NewAttendeeInformation>;
  attendeeInfoCheckIn: Array<NewAttendeeInformation>;
  hideContentForClientNeeds: boolean;
  hidePassportRewardAndCheckinsInfo: boolean;

  constructor(source?: IBtrPassportApiModel) {
    super();
    if (source == null) {
      return;
    }
    this.id = source.id;
    this.slug = source.slug;
    this.hideContentForClientNeeds = [
      'taste-of-eastie-demo-savory',
      'survey-test-data',
      'test-0710',
      'start-of-spring-week',
    ].includes(this.slug);
    this.hidePassportRewardAndCheckinsInfo = [
      'lic-perks-pass',
      'survey-test-data',
    ].includes(this.slug);
    this.title = source.title;
    this.summary = source.summary;
    this.description = source.description_page?.body?.length
      ? source.description_page?.body
      : source.description;
    this.isPublished = source.is_published;
    this.isRefundStripeFeeOnClient = source.is_refund_stripe_fee_on_client;
    this.rawStart = source.start;
    this.rawEnd = source.end;
    this.start = source.timezone
      ? new Date(source.start).toLocaleString('en-Us', {
          timeZone: source.timezone,
        })
      : source.start;
    this.end =
      source.end && source.timezone
        ? new Date(source.end).toLocaleString('en-Us', {
            timeZone: source.timezone,
          })
        : source.end;
    this.isExpired =
      source.is_expired && !source.is_deleted && source.is_published;
    this.timezone = source.timezone;
    this.noStartTime = source.no_start_time;
    this.noEndTime = source.no_end_time;
    this.datePublication = source.timezone
      ? new Date(source.date_publication).toLocaleString('en-Us', {
          timeZone: source.timezone,
        })
      : source.date_publication;
    this.dateArchiving = source.timezone
      ? new Date(source.date_archiving).toLocaleString('en-Us', {
          timeZone: source.timezone,
        })
      : source.date_archiving;
    this.dateCreation = source.timezone
      ? new Date(source.date_creation).toLocaleString('en-Us', {
          timeZone: source.timezone,
        })
      : source.date_creation;
    this.image = source.image;
    this.contactEmail = source.contact_email;
    this.contactPhone = source.contact_phone;
    this.footnotes = source.footnotes;
    this._places = source.beacons;
    this.onlineEventDetails = source.online_event_details;
    this.onlineEventLink = source.online_event_link;
    this._tickets = source.tickets?.map(ticket => {
      if (ticket.start && ticket.end) {
        return {
          ...ticket,
          start: source.timezone
            ? new Date(ticket.start).toLocaleString('en-Us', {
                timeZone: source.timezone,
              })
            : ticket.start,
          end: source.timezone
            ? new Date(ticket.end).toLocaleString('en-Us', {
                timeZone: source.timezone,
              })
            : ticket.end,
          statuses: null,
          event: ticket?.event ?? source.id,
        };
      }
      return { ...ticket, event: ticket?.event ?? source.id };
    });
    this.isDeleted = source.is_deleted;
    this.attendees =
      source.attendee_info_fields?.length > 0 ||
      source.attendee_info_fields_required?.length > 0
        ? Object.keys(EventAttendeeTypeEnum)
            ?.map(key => ({
              type: EventAttendeeTypeEnum[key],
              required:
                source.attendee_info_fields_required?.includes(
                  EventAttendeeTypeEnum[key],
                ) || false,

              static: false,
              include:
                source.attendee_info_fields?.includes(
                  EventAttendeeTypeEnum[key],
                ) ||
                source.attendee_info_fields_required?.includes(
                  EventAttendeeTypeEnum[key],
                ) ||
                false,
            }))
            .filter(item => item.include)
        : [];
    this.purchaseConfirmationMessage =
      source.purchase_confirmation_message ||
      'You have successfully placed your order! Please check your email for your ticket and QR code. If you cannot find your tickets, please check your spam or junk folder! ';
    this.purchaseSupportEmail = source.purchase_support_email;
    this.purchaseEmailMessage =
      source.purchase_email_message ||
      'Thank you for your successful order. We hope that you enjoy!';
    this.isAttendeeInfoForEachTicket =
      source.is_attendee_info_for_each_ticket ?? false;
    this.representation = source.representation;
    this.showAlone = source.show_alone ?? false;
    this.showInWhiteLabel = source.show_alone
      ? source.show_in_white_label
      : true;
    this.whiteLabelName = source.white_label_name;
    this.locations =
      source.locations?.map(item => new LocationModel(item)) ?? [];
    this.formula = source?.formula
      ? {
          coefs:
            source.formula.coefs?.map(item => ({
              platformFixed: item.platform_fixed,
              platformPersentage: item.platform_persentage,
              stripeFixed: item.stripe_fixed,
              stripePersentage: item.stripe_persentage,
              threshold: item.threshold,
            })) ?? [],
          isProgressive: source.formula.is_progressive,
          isDefault: source.formula.is_default,
          name: source.formula.name,
        }
      : null;
    this.passportBeaconGroups = mapPassportBeaconGroups(
      source.passport_beacon_groups,
    );
    this.verificationTiers = mapVerificationTiers(source.verification_tiers);
    this.descriptionPage = new BtrStaticPageModel(source.description_page);
    if (
      this.description?.length > 0 &&
      this.descriptionPage.body?.length === 0
    ) {
      this.descriptionPage.body = this.description;
    }

    this.isPassport = true;
    this.attendeeInfo =
      source.attendee_info
        ?.filter(info => info.is_visible)
        ?.map(info => new NewAttendeeInformation(info)) ?? [];
    this.attendeeInfoCheckIn =
      source.attendee_info_checkin
        ?.filter(info => info.is_visible)
        ?.map(info => new NewAttendeeInformation(info)) ?? [];
  }

  static copyObj(
    source: Pick<BtrPassportModel, keyof BtrPassportModel>,
  ): BtrPassportModel {
    return Object.assign(new BtrPassportModel(), source);
  }

  static formAttendeeToServer(
    attendee: NewAttendeeInformation,
    ticketIndex: number,
  ): Array<IAttendeeItemToServer> {
    if (attendee.otherValue?.length) {
      return [
        {
          key: `ticket-${ticketIndex}-${attendee.slug}`,
          value: attendee.value,
        },
        {
          key: `ticket-${ticketIndex}-${
            attendee.slugs[attendee.slugs.length - 1]
          }`,
          value: attendee.otherValue,
        },
      ];
    }
    return [
      {
        key: `ticket-${ticketIndex}-${attendee.slug}`,
        value: attendee.value,
      },
    ];
  }

  static formTicketAttendeeToServer(
    item: TicketModel,
    index: number,
  ): IAttendeeItemToServer {
    return {
      key: `ticket-${index}-ticket`,
      value: item.id,
    };
  }

  updateTicketStatuses(id: string, statuses: Array<IBmTicketStatus>): void {
    this._tickets = this._tickets?.map(ticket => {
      if (ticket.id === id) {
        return {
          ...ticket,
          statuses,
        };
      }
      return ticket; // Add this line to return the ticket if the condition is not met
    });
  }

  updateTicketStatusVerification(check_id: string, beacon_id: number): void {
    this._tickets = this._tickets?.map(ticket => {
      ticket.statuses.map(status => {
        if (
          status.check_id === check_id &&
          !status.verifications.map(ver => ver.beacon_id).includes(beacon_id)
        ) {
          status.verifications.push({
            date: new Date().toISOString(),
            beacon_id: beacon_id,
          });
        }
      });
      return ticket;
    });
  }
}

export enum PassportFilterType {
  NONE = 'none',
  PASSPORT = 'passport',
}
